/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * One post in a cooking group.
 *
 * ── WHAT THIS REPLACED ──────────────────────────────────────────────────────
 * A `<li>` in a bordered list, inside a `max-h-[60vh]` scroller, with a 24×24px
 * photo thumbnail. Someone's roast — the thing the whole feature exists to
 * share — rendered smaller than the timestamp beside it. The card also tinted
 * your own household's posts with a background wash, which is chat-app
 * convention and wrong here: in a group of six families, "who said this"
 * matters far more than "was it me".
 *
 * ── THE PHOTO LEADS ─────────────────────────────────────────────────────────
 * A post with a picture puts the picture first, edge to edge, above the words.
 * That is the Instagram arrangement and it is the right one for this content:
 * "here's what I actually cooked" is a visual claim, and the caption is a
 * gloss on it. A post WITHOUT a photo is not a broken version of that — it
 * renders as a compact text card, so a discussion-heavy group doesn't look
 * like a feed full of holes.
 *
 * ── REACTIONS ARE OPTIMISTIC ────────────────────────────────────────────────
 * Tapping a reaction updates the count before the request lands, because a
 * half-second delay on a like makes the button feel broken and people tap it
 * again. The server returns the full summary and we reconcile — see the note
 * in CirclesApi.react.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { CirclesApi } from "../../api/endpoints.ts";
import type { CircleMessage, ReactionKind, ReactionSummary, RecipeRef } from "../../api/types.ts";
import { Icon, type IconName } from "../../components/Icon.tsx";
import { ActionMenu } from "../../components/ActionMenu.tsx";
import { Modal } from "../../components/ui.tsx";
import { useToast } from "../../components/Toast.tsx";
import { usePrefs } from "../../i18n/index.tsx";
import { CirclePhoto } from "./CirclePhoto.tsx";
import { recipeRefDestination } from "./recipeTag.ts";

/**
 * The three things a cooking group says to a post.
 *
 * Deliberately not one "like". "Looks lovely", "I made it" and "I'm going to
 * make it" are three different statements, and a recipe group that collapses
 * them loses exactly the information it exists to carry — see migration 032.
 */
const REACTIONS: Array<{ kind: ReactionKind; icon: IconName; label: string; verb: string }> = [
  { kind: "yum", icon: "star", label: "Yum", verb: "said yum" },
  { kind: "made_it", icon: "check", label: "Made it", verb: "made this" },
  { kind: "want_to_try", icon: "clock", label: "Want to try", verb: "want to try this" },
];

/** "3 minutes ago" from a server timestamp. Relative time is a display choice. */
function ago(iso: string): string {
  const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/** A stable colour per author, so the same household looks the same each time. */
function avatarTone(label: string): string {
  const tones = [
    "bg-accent/15 text-accent",
    "bg-teal/15 text-teal",
    "bg-navy/15 text-navy",
    "bg-ok/15 text-ok",
    "bg-warn/20 text-ink-2",
  ];
  let hash = 0;
  for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) | 0;
  return tones[Math.abs(hash) % tones.length]!;
}

export function PostCard({ post, circleId, canPost, isOwner, onLightbox }: {
  post: CircleMessage;
  circleId: string;
  canPost: boolean;
  isOwner: boolean;
  onLightbox: (url: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = usePrefs();
  const [showReplies, setShowReplies] = useState(false);
  const [replyText, setReplyText] = useState("");
  // The "available" tag's preview dialog; yours/accepted navigate directly.
  const [tagPreview, setTagPreview] = useState<RecipeRef | null>(null);

  // Optimistic reaction state, layered over whatever the last poll delivered.
  const [pending, setPending] = useState<Map<ReactionKind, ReactionSummary>>(new Map());

  const photos = post.photoIds ?? [];
  const serverReactions = post.reactions ?? [];
  const replyCount = post.replyCount ?? 0;

  const reactionFor = (kind: ReactionKind): ReactionSummary =>
    pending.get(kind)
    ?? serverReactions.find((r) => r.kind === kind)
    ?? { kind, count: 0, mine: false };

  const react = useMutation({
    mutationFn: ({ kind, on }: { kind: ReactionKind; on: boolean }) =>
      CirclesApi.react(circleId, post.id, kind, on),
    onSuccess: ({ reactions }) => {
      // Reconcile: drop the optimistic layer and take the server's word.
      setPending(new Map(reactions.map((r) => [r.kind, r])));
      void qc.invalidateQueries({ queryKey: ["circle-messages", circleId] });
    },
    onError: (e: Error) => {
      setPending(new Map());   // roll back to whatever the feed last knew
      toast(e.message, "danger");
    },
  });

  const toggle = (kind: ReactionKind) => {
    if (!canPost) return;
    const current = reactionFor(kind);
    const next = !current.mine;
    setPending((m) => new Map(m).set(kind, {
      kind,
      count: Math.max(0, current.count + (next ? 1 : -1)),
      mine: next,
    }));
    react.mutate({ kind, on: next });
  };

  const replies = useQuery({
    queryKey: ["circle-replies", circleId, post.id],
    queryFn: () => CirclesApi.replies(circleId, post.id),
    enabled: showReplies,
  });

  const sendReply = useMutation({
    mutationFn: () => CirclesApi.postMessage(circleId, {
      body: replyText.trim(), replyTo: post.id,
    }),
    onSuccess: () => {
      setReplyText("");
      void qc.invalidateQueries({ queryKey: ["circle-replies", circleId, post.id] });
      void qc.invalidateQueries({ queryKey: ["circle-messages", circleId] });
    },
    onError: (e: Error) => toast(e.message, "danger"),
  });

  const remove = useMutation({
    mutationFn: () => CirclesApi.deleteMessage(circleId, post.id),
    onSuccess: () => {
      toast("Post removed", "ok");
      void qc.invalidateQueries({ queryKey: ["circle-messages", circleId] });
    },
    onError: (e: Error) => toast(e.message, "danger"),
  });

  const flag = useMutation({
    mutationFn: () => CirclesApi.flagMessage(circleId, post.id),
    onSuccess: () => toast("Reported to the group's owner", "ok"),
    onError: (e: Error) => toast(e.message, "danger"),
  });

  // The SharedRecipeStrip's accept mutation, reused for a tag in "available"
  // state: accepting lands the reader on their own copy, like the strip does.
  const acceptTag = useMutation({
    mutationFn: (recipeId: string) => CirclesApi.library.recipes.accept(circleId, recipeId),
    onSuccess: ({ recipe }) => {
      setTagPreview(null);
      toast(t("circles.recipeAccepted"), "ok");
      void qc.invalidateQueries({ queryKey: ["circle-library-shelf", circleId] });
      void qc.invalidateQueries({ queryKey: ["circle-library-recipes", circleId] });
      void qc.invalidateQueries({ queryKey: ["circle-messages", circleId] });
      void qc.invalidateQueries({ queryKey: ["recipes"] });
      navigate(`/recipes/${recipe.id}`);
    },
    onError: (e: Error) => toast(e.message, "danger"),
  });

  // ── withdrawn ─────────────────────────────────────────────────────────────
  // Kept in place rather than removed. A gap where a post was is confusing when
  // its replies are still beneath it, and 027's soft delete means the words are
  // genuinely gone — there is nothing here to hide.
  if (post.deleted) {
    return (
      <article className="card p-4">
        <p className="text-sm italic text-ink-3">
          {post.deletedByOwner ? "Removed by the group's owner." : "This post was withdrawn."}
        </p>
        {replyCount > 0 && (
          <button className="mt-2 text-xs text-accent hover:underline"
                  onClick={() => setShowReplies((s) => !s)}>
            {showReplies ? "Hide" : "Show"} {replyCount} {replyCount === 1 ? "reply" : "replies"}
          </button>
        )}
        {showReplies && <ReplyThread
          replies={replies.data?.replies ?? []} loading={replies.isLoading}
          value={replyText} onChange={setReplyText}
          onSend={() => sendReply.mutate()} canPost={canPost} sending={sendReply.isPending} />}
      </article>
    );
  }

  const initial = post.authorLabel.trim().charAt(0).toUpperCase() || "?";

  return (
    <article className="card overflow-hidden">
      {/* ── who and when ─────────────────────────────────────────────────── */}
      <header className="flex items-center gap-2.5 px-4 py-3">
        <span aria-hidden
              className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-semibold ${
                avatarTone(post.authorLabel)}`}>
          {initial}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-ink">{post.authorLabel}</span>
            {post.isOwnHousehold && (
              <span className="chip shrink-0 !py-0 text-[10px]">you</span>
            )}
          </div>
          <time className="text-xs text-ink-3" dateTime={post.createdAt}>
            {ago(post.createdAt)}{post.editedAt && " · edited"}
          </time>
        </div>
        <ActionMenu
          label={`Actions for ${post.authorLabel}'s post`}
          items={[
            ...(post.isMine || isOwner
              ? [{
                  label: "Delete this post", icon: "archive" as const, destructive: true,
                  onSelect: () => {
                    if (confirm("Delete this post? The text and photos are removed for everyone."))
                      remove.mutate();
                  },
                }]
              : [{
                  label: "Report to the owner", icon: "alert" as const,
                  hint: "Flags it for review", onSelect: () => flag.mutate(),
                }]),
          ]}
        />
      </header>

      {/* ── the dish, if one is named ────────────────────────────────────── */}
      {/* A tagged recipe (one identity per post — the composer never sends
          both) replaces the free-text "a dish they mentioned" row with a link
          to the actual shared recipe. */}
      {post.recipeRef ? (
        <RecipeTagRow r={post.recipeRef} onPreview={setTagPreview} />
      ) : post.suggestedTitle ? (
        <div className="mx-4 mb-3 flex items-start gap-2 rounded-lg border border-accent/30 bg-accent/5 px-3 py-2">
          <Icon name="book" className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink">{post.suggestedTitle}</div>
            {/* Saying this out loud matters. 027 is deliberate that naming a
                dish is not sharing the recipe, and a user who assumes otherwise
                will wonder why it never turned up in their library. */}
            <div className="text-xs text-ink-3">
              A dish they mentioned — not a shared recipe
            </div>
          </div>
        </div>
      ) : null}

      {/* ── the photos, leading ──────────────────────────────────────────── */}
      {photos.length > 0 && (
        <div className={`grid gap-0.5 ${
          photos.length === 1 ? "grid-cols-1"
          : photos.length === 2 ? "grid-cols-2"
          : "grid-cols-2"}`}>
          {photos.map((pid, i) => (
            <div key={pid}
                 className={photos.length === 3 && i === 0 ? "col-span-2" : ""}>
              <CirclePhoto
                circleId={circleId} messageId={post.id} photoId={pid}
                onOpen={onLightbox}
                aspect={photos.length === 1 ? "wide" : "square"}
              />
            </div>
          ))}
        </div>
      )}

      {/* ── the caption ──────────────────────────────────────────────────── */}
      {post.body && (
        <p className="whitespace-pre-wrap px-4 pt-3 text-sm leading-relaxed text-ink">
          {post.body}
        </p>
      )}

      {/* ── reactions ────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-1.5 px-3 py-3">
        {REACTIONS.map((r) => {
          const state = reactionFor(r.kind);
          return (
            <button
              key={r.kind}
              type="button"
              disabled={!canPost}
              aria-pressed={state.mine}
              aria-label={`${r.label}${state.count ? ` — ${state.count} so far` : ""}`}
              onClick={() => toggle(r.kind)}
              className={`inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-full border px-3 text-xs
                          transition disabled:opacity-40 ${
                state.mine
                  ? "border-accent bg-accent/15 font-medium text-accent"
                  : "border-rule-soft text-ink-2 hover:border-accent/50 hover:text-ink"}`}
            >
              <Icon name={state.mine && r.kind === "yum" ? "starFilled" : r.icon}
                    className="h-3.5 w-3.5" />
              {r.label}
              {state.count > 0 && <span className="tabular-nums">{state.count}</span>}
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => setShowReplies((s) => !s)}
          className="ml-auto inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-full px-3 text-xs text-ink-2 transition hover:bg-paper-3 hover:text-ink"
        >
          <Icon name="chat" className="h-3.5 w-3.5" />
          {replyCount === 0 ? "Reply" : `${replyCount} ${replyCount === 1 ? "reply" : "replies"}`}
        </button>
      </div>

      {showReplies && (
        <ReplyThread
          replies={replies.data?.replies ?? []}
          loading={replies.isLoading}
          value={replyText}
          onChange={setReplyText}
          onSend={() => sendReply.mutate()}
          canPost={canPost}
          sending={sendReply.isPending}
        />
      )}

      {/* The "available" tag's preview: the library metadata and the same
          accept action the SharedRecipeStrip offers. */}
      {tagPreview && (
        <Modal title={tagPreview.title} onClose={() => setTagPreview(null)}>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {tagPreview.imageUrl && (
                <img src={tagPreview.imageUrl} alt=""
                     className="h-14 w-14 rounded-lg object-cover" />
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink">{tagPreview.title}</div>
                <div className="text-xs text-ink-3">{tagPreview.ownerLabel}</div>
              </div>
            </div>
            <button
              className="btn-ghost !py-1.5 text-xs"
              disabled={acceptTag.isPending}
              onClick={() => acceptTag.mutate(tagPreview.recipeId)}
            >
              <Icon name="plus" className="h-3.5 w-3.5" />
              Add to my library
            </button>
          </div>
        </Modal>
      )}
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A tagged recipe on a post. Where the row leads depends on the READER's
 * relationship to the recipe (resolved per-reader on the server, S-3):
 * yours/accepted open the reader's own copy; available opens the preview
 * dialog; unavailable — the share was revoked or the recipe deleted — is a
 * plain row with the post-time snapshot title, not a link.
 */
function RecipeTagRow({ r, onPreview }: {
  r: RecipeRef;
  onPreview: (r: RecipeRef) => void;
}) {
  const { t } = usePrefs();
  const navigate = useNavigate();
  const dest = recipeRefDestination(r);

  const inner = (
    <>
      {r.imageUrl ? (
        <img src={r.imageUrl} alt="" className="h-9 w-9 shrink-0 rounded-md object-cover" />
      ) : (
        <Icon name="book" className={`h-4 w-4 shrink-0 ${
          r.status === "unavailable" ? "text-ink-3" : "text-accent"}`} />
      )}
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-ink">{r.title}</div>
        <div className="text-xs text-ink-3">
          {r.status === "unavailable"
            ? t("circles.recipeUnavailable")
            : `${t("circles.sharedRecipe")} · ${r.ownerLabel}`}
        </div>
      </div>
    </>
  );

  if (!dest && r.status !== "available") {
    // unavailable — plain row, no link styling.
    return (
      <div className="mx-4 mb-3 flex items-center gap-2 rounded-lg border border-rule-soft px-3 py-2">
        {inner}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => (dest ? navigate(dest) : onPreview(r))}
      className="mx-4 mb-3 flex w-[calc(100%-2rem)] items-center gap-2 rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-left transition hover:border-accent/60"
    >
      {inner}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ReplyThread({ replies, loading, value, onChange, onSend, canPost, sending }: {
  replies: CircleMessage[];
  loading: boolean;
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  canPost: boolean;
  sending: boolean;
}) {
  return (
    <div className="border-t border-rule-soft bg-paper-3/40 px-4 py-3">
      {loading && <p className="text-xs text-ink-3">Loading replies…</p>}

      {replies.length > 0 && (
        <ul className="mb-3 space-y-2.5">
          {replies.map((r) => (
            <li key={r.id} className="flex gap-2.5">
              <span aria-hidden
                    className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-[10px] font-semibold ${
                      avatarTone(r.authorLabel)}`}>
                {r.authorLabel.trim().charAt(0).toUpperCase() || "?"}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-medium text-ink">{r.authorLabel}</span>
                  <time className="text-[11px] text-ink-3" dateTime={r.createdAt}>
                    {ago(r.createdAt)}
                  </time>
                </div>
                <p className={`whitespace-pre-wrap text-sm ${
                  r.deleted ? "italic text-ink-3" : "text-ink-2"}`}>
                  {r.deleted ? "Withdrawn." : r.body}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {canPost && (
        <form
          className="flex gap-2"
          onSubmit={(e) => { e.preventDefault(); if (value.trim()) onSend(); }}
        >
          <input
            className="input !py-1.5 text-sm"
            placeholder="Write a reply…"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label="Write a reply"
          />
          <button className="btn-ghost shrink-0 !py-1.5" disabled={!value.trim() || sending}>
            {sending ? "…" : "Reply"}
          </button>
        </form>
      )}
    </div>
  );
}
